import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, XCircle, SearchX, CheckCircle2, UploadCloud, BookUser, Camera } from "lucide-react";
import GamcaSummaryCard from "@/components/GamcaSummaryCard";
import { toast } from "sonner";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import SearchableSelect from "@/components/SearchableSelect";

const SEARCH_STEPS = ["Connecting to WAFID system", "Checking cancellation availability"];

export default function GamcaCancellationSearch({ onResult }) {
  const [nationalities, setNationalities] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [extractError, setExtractError] = useState("");
  const [manualNationality, setManualNationality] = useState("");
  const [searching, setSearching] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/gamca/nationalities").then(({ data }) => {
      const seen = new Set();
      setNationalities(data.filter((n) => !seen.has(n.label) && seen.add(n.label)).map((n) => ({ value: n.label, label: n.label })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!searching) return;
    setStep(0);
    const t = setInterval(() => setStep((s) => Math.min(s + 1, SEARCH_STEPS.length - 1)), 7000);
    return () => clearInterval(t);
  }, [searching]);

  const handleSearch = async (pass, natl, passportUrl) => {
    setSearching(true);
    setResult(null);
    onResult(null);
    try {
      const { data } = await api.post("/gamca/search-slip", { passport: pass, nationality: natl });
      setResult(data);
      onResult({ ...data, passport: pass.toUpperCase(), nationality: natl, passport_copy: passportUrl });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setExtracted(null);
    setExtractError("");
    setManualNationality("");
    setResult(null);
    onResult(null);
    try {
      const upload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", upload);
      const { data } = await api.post("/gamca/extract-passport", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.is_passport || !data.passport_number) {
        setExtractError(data.message);
        toast.error(data.message);
        return;
      }
      setExtracted(data);
      if (data.nationality) {
        handleSearch(data.passport_number, data.nationality, data.url);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not read the passport. Please try again.";
      setExtractError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const d = result?.details || {};

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-[#1A1A1A] mb-1.5">Upload Passport First Page</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label
            data-testid="gcc-cancel-passport-upload"
            className="flex items-center gap-2 border border-dashed border-[var(--brand-primary)]/50 rounded-md px-4 py-3.5 cursor-pointer hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_95%)] transition-colors"
          >
            <input type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" className="hidden" onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ""; }} />
            {uploading ? <Loader2 size={17} className="animate-spin text-[var(--brand-primary)]" /> : <UploadCloud size={17} className="text-[var(--brand-primary)]" />}
            <span className="text-sm text-[var(--brand-secondary)]">
              {uploading ? "Verifying passport details…" : extracted ? "Upload a different passport" : "Click to upload (JPG, PNG, PDF - max 5MB)"}
            </span>
          </label>
          <label
            data-testid="gcc-cancel-passport-camera"
            className="flex items-center gap-2 border border-dashed border-[var(--brand-primary)]/50 rounded-md px-4 py-3.5 cursor-pointer hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_95%)] transition-colors"
          >
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ""; }} />
            <Camera size={17} className="text-[var(--brand-primary)]" />
            <span className="text-sm text-[var(--brand-secondary)]">Take photo with camera</span>
          </label>
        </div>
      </div>

      {extractError && !uploading && (
        <div data-testid="gcc-cancel-extract-error" className="rounded-lg border border-red-200 bg-red-50/80 px-4 py-3 text-xs font-medium text-red-600 flex items-start gap-2">
          <XCircle size={15} className="shrink-0 mt-0.5" /> {extractError}
        </div>
      )}

      {extracted && !extracted.nationality && !manualNationality && !uploading && (
        <div data-testid="gcc-cancel-extracted-card" className="rounded-lg border border-[#E9E4DB] bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <BookUser size={16} className="text-[var(--brand-primary)] shrink-0" />
            <p className="text-xs font-bold text-[#1A1A1A]">Confirm your nationality to continue</p>
          </div>
          <SearchableSelect label="" options={nationalities} value={manualNationality}
            onChange={(v) => { setManualNationality(v); if (v) handleSearch(extracted.passport_number, v, extracted.url); }}
            placeholder="Search nationality" testId="gcc-cancel-nationality" />
        </div>
      )}

      {searching && (
        <div data-testid="gcc-cancel-searching-card" className="rounded-lg border border-[#E9E4DB] bg-white px-4 py-3.5 shadow-sm">
          <div className="flex items-center gap-2 mb-2.5">
            <Loader2 size={15} className="animate-spin text-[var(--brand-primary)]" />
            <p className="text-sm font-semibold text-[#1A1A1A]">Searching on WAFID system…</p>
          </div>
          <div className="space-y-1.5">
            {SEARCH_STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2 text-xs">
                {i < step ? <CheckCircle2 size={13} className="text-green-600 shrink-0" />
                  : i === step ? <Loader2 size={13} className="animate-spin text-[var(--brand-primary)] shrink-0" />
                  : <span className="w-[13px] h-[13px] rounded-full border border-[#D8D2C6] shrink-0" />}
                <span className={i < step ? "text-green-700 font-medium" : i === step ? "text-[#1A1A1A] font-medium" : "text-[var(--brand-secondary)]/60"}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && !searching && !result.found && (
        <div data-testid="gcc-cancel-notfound-card" className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3.5">
          <div className="flex items-start gap-2.5">
            <SearchX size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">No Slip Found</p>
              <p data-testid="gcc-cancel-message" className="text-xs text-amber-700 mt-0.5">{result.message}</p>
            </div>
          </div>
        </div>
      )}

      {result && !searching && result.found && (
        <>
          <div data-testid="gcc-cancel-status-card" className={`rounded-lg border px-4 py-3.5 ${
            result.cancellable ? "border-green-200 bg-green-50/70"
            : result.already_cancelled ? "border-[#D8D2C6] bg-[#FAF8F4]"
            : result.status_unknown ? "border-amber-200 bg-amber-50/80"
            : "border-red-200 bg-red-50/80"}`}>
            <div className="flex items-start gap-2.5">
              {result.cancellable ? <ShieldCheck size={18} className="text-green-600 shrink-0 mt-0.5" />
                : result.already_cancelled ? <CheckCircle2 size={18} className="text-[var(--brand-secondary)] shrink-0 mt-0.5" />
                : result.status_unknown ? <SearchX size={18} className="text-amber-600 shrink-0 mt-0.5" />
                : <XCircle size={18} className="text-red-500 shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <p className={`text-sm font-bold ${
                  result.cancellable ? "text-green-800"
                  : result.already_cancelled ? "text-[#1A1A1A]"
                  : result.status_unknown ? "text-amber-800"
                  : "text-red-700"}`}>
                  {result.cancellable ? "Cancellation Available"
                    : result.already_cancelled ? "Already Cancelled"
                    : result.status_unknown ? "Cancellation Not Available"
                    : "Cancellation Not Possible"}
                </p>
                <p data-testid="gcc-cancel-message" className={`text-xs mt-0.5 ${
                  result.cancellable ? "text-green-700"
                  : result.already_cancelled ? "text-[var(--brand-secondary)]"
                  : result.status_unknown ? "text-amber-700"
                  : "text-red-600"}`}>
                  {result.message}
                </p>
                {!result.cancellable && !result.already_cancelled && !result.status_unknown && result.wafid_status && (
                  <div data-testid="gcc-cancel-wafid-status" className="mt-2 inline-flex items-center gap-2.5 rounded-lg border border-red-200 bg-white px-3 py-2 shadow-sm">
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--brand-secondary)]">Current Status</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 text-white ${
                      result.wafid_status === "fit" ? "bg-green-600" : result.wafid_status === "unfit" ? "bg-red-600" : "bg-amber-500"}`}>
                      {result.wafid_status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <GamcaSummaryCard testPrefix="gcc-cancel-details-card" summary={{
            type: "cancellation", full_name: d.full_name, passport: d.passport_no,
            nationality: d.nationality, slip_no: d.slip_no,
            medical_center: d.medical_center, traveling_to: d.traveling_to, valid_till: d.valid_till,
          }} />
        </>
      )}
    </div>
  );
}
